import { MarketId, MarketKey } from '@velocity-exchange/common';
import { PerpMarketAccount } from '@velocity-exchange/sdk';
import { create } from 'zustand';
import { Live24hMarketData } from './types';

/**
 * Market "stats" slices of the live-state module (stage 4): 24h summary,
 * predicted funding, and perp-market-account info.
 *
 * These are deliberately kept OUT of the slot-arbitrated 250ms price store
 * (marketLiveStateCore.ts). They are single-producer, slow-cadence, and read as
 * whole-market collections (24h data, predicted fundings) — putting them in the
 * price store would re-render every collection consumer on each ~250ms price
 * flush. Here each slice has exactly one producer and its own reference, so a
 * consumer only re-renders when the slice it reads actually changes.
 *
 * `data24h` / `predictedFundings` are whole-collection arrays (replaced on each
 * producer update, matching the legacy VelocityStore fields they replace).
 * `perpInfo` is keyed per market because it is only ever read per-market (by the
 * market-info display derivation).
 */

export type PredictedFunding = { marketIndex: number; fundingRate: number };

/** A perp market account plus the chain slot it was observed at (for arbitration). */
type PerpInfoEntry = { account: PerpMarketAccount; slot: number };

interface MarketStatsStore {
	data24h: Live24hMarketData[];
	predictedFundings: PredictedFunding[];
	perpInfo: Record<MarketKey, PerpInfoEntry>;
}

const useMarketStatsStore = create<MarketStatsStore>(() => ({
	data24h: [],
	predictedFundings: [],
	perpInfo: {},
}));

// --- Write seam for the stats slices (producers/adapters only) -------------

/** Publish the full 24h market-summary collection. */
export const publish24hMarketData = (data: Live24hMarketData[]): void => {
	useMarketStatsStore.setState({ data24h: data });
};

/** Publish the full predicted-funding collection. */
export const publishPredictedFundings = (
	fundings: PredictedFunding[]
): void => {
	useMarketStatsStore.setState({ predictedFundings: fundings });
};

/**
 * Publish a single perp market's on-chain account, arbitrated by the slot it
 * was observed at: a stale (lower-or-equal slot) republish is ignored, so the
 * stored reference only changes when a strictly newer account lands. This is
 * the same highest-slot-wins rule the price store uses, and it keeps reactive
 * readers from recomputing on a no-op republish at the same slot.
 */
export const publishPerpMarketInfo = (
	marketId: MarketId,
	account: PerpMarketAccount,
	slot: number
): void => {
	const key = marketId.key;
	const prev = useMarketStatsStore.getState().perpInfo[key];
	if (prev && slot <= prev.slot) return;

	useMarketStatsStore.setState((s) => ({
		perpInfo: { ...s.perpInfo, [key]: { account, slot } },
	}));
};

// --- Imperative reads ------------------------------------------------------

export const getLive24hMarketData = (): Live24hMarketData[] =>
	useMarketStatsStore.getState().data24h;

export const getLivePredictedFundings = (): PredictedFunding[] =>
	useMarketStatsStore.getState().predictedFundings;

export const getLivePerpMarketInfo = (
	marketId: MarketId | undefined
): PerpMarketAccount | undefined =>
	marketId
		? useMarketStatsStore.getState().perpInfo[marketId.key]?.account
		: undefined;

// --- Reactive reads --------------------------------------------------------

export const useLive24hMarketData = (): Live24hMarketData[] =>
	useMarketStatsStore((s) => s.data24h);

export const useLivePredictedFundings = (): PredictedFunding[] =>
	useMarketStatsStore((s) => s.predictedFundings);

export const useLivePerpMarketInfo = (
	marketId: MarketId | undefined
): PerpMarketAccount | undefined =>
	// The stored entry only gets a new reference when a strictly-newer-slot
	// account replaces it (see publishPerpMarketInfo), so the `.account` ref is
	// stable across no-op republishes and default identity equality is enough.
	useMarketStatsStore((s) =>
		marketId ? s.perpInfo[marketId.key]?.account : undefined
	);

/** Test-only: wipe all stats-slice state. */
export const __resetMarketStatsForTests = (): void => {
	useMarketStatsStore.setState({
		data24h: [],
		predictedFundings: [],
		perpInfo: {},
	});
};
